import { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $backgroundTasksStore, $cardLimitStore, $cardStore, $notificationStore } from '@lib/store';
import { useCurrencySymbol } from '@shared/hooks';
import { BackgroundTasksStatus, CardKind, CreditCardLimit, CreditLimitsResponse, DebitLimitResponse, Frequency } from '@shared/api';
import { CardNewLimitRow } from '@widgets';
import { Button, Modal, Select } from '@shared/widgets';

const getDefaultLimits = (cardKind: CardKind, limits: CreditLimitsResponse | DebitLimitResponse) => {
  if (cardKind === CardKind.Credit)
    return {
      frequency: limits.frequency,
      retailAmount: limits.currentRetailAmount,
      retailNumber: limits.currentRetailNumber,
      cashAmount: limits.currentCashAmount,
      cashNumber: limits.currentCashNumber,
      otcAmount: (limits as CreditLimitsResponse).currentOTCAmount,
      otcNumber: (limits as CreditLimitsResponse).currentOTCNumber,
    };
  return {
    frequency: limits.frequency,
    retailAmount: limits.currentRetailAmount,
    retailNumber: limits.currentRetailNumber,
    cashAmount: limits.currentCashAmount,
    cashNumber: limits.currentCashNumber,
  };
};

const POLLING_INTERVAL = 2000;
const MAX_POLLING_TIME = 60000;
let TIMER_ID: NodeJS.Timeout | null = null;
interface IForm {
  frequency: Frequency;
  retailAmount: number;
  retailNumber: number;
  cashAmount: number;
  cashNumber: number;
  otcAmount: number;
  otcNumber: number;
}

interface RequestNewLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RequestNewLimitsModal = (props: RequestNewLimitsModalProps) => {
  const cardStore = useUnit($cardStore.store);
  const cardLimitsStore = useUnit($cardLimitStore.store);
  const card = cardStore.data!;
  const limits = cardLimitsStore.data!;

  const [loading, setLoading] = useState<boolean>(false);

  const formControl = useForm<IForm>({ defaultValues: getDefaultLimits(card.cardKind, limits) });
  const { control, formState, watch, handleSubmit, reset } = formControl;
  const frequency = watch('frequency');

  const { t } = useTranslation('card');

  const closeHandler = () => {
    props.onClose();
    reset(getDefaultLimits(card.cardKind, limits));
    if (TIMER_ID) {
      clearInterval(TIMER_ID);
      TIMER_ID = null;
      setLoading(false);
    }
  };
  const submitHandler = async (data: IForm) => {
    if (card.cardKind === CardKind.Credit) {
      await $cardLimitStore.events.updateCreditLimit({ ...data, cardUUId: card.cardGuid });
      props.onClose();
    }
    if (card.cardKind === CardKind.Debit) {
      setLoading(true);
      const result = await $cardLimitStore.events.updateDebitLimit({
        cardUUId: card.cardGuid,
        frequency: data.frequency,
        retailAmount: +data.retailAmount,
        retailNumber: +data.retailNumber,
        cashAmount: +data.cashAmount,
        cashNumber: +data.cashNumber,
      });
      const startedAt = Date.now();
      if (result.data?.taskId) {
        TIMER_ID = setInterval(async () => {
          const elapsed = Date.now() - startedAt;
          if (elapsed >= MAX_POLLING_TIME) {
            clearInterval(TIMER_ID!);
            TIMER_ID = null;
            setLoading(false);
            $notificationStore.events.setError('Something went wrong while updating Card Limits. Please try again.');
            return;
          }

          try {
            const result2 = await $backgroundTasksStore.events.getBackgroundTasks(result.data!.taskId);
            if (result2.data?.status === BackgroundTasksStatus.Completed) {
              clearInterval(TIMER_ID!);
              $cardLimitStore.events.getLimit({ id: card.cardId, cardKind: card.cardKind });
              setLoading(false);
              props.onClose();
            }
          } catch {
            clearInterval(TIMER_ID!);
            TIMER_ID = null;
            setLoading(false);
            $notificationStore.events.setError('Something went wrong while updating Card Limits. Please try again.');
          }
        }, POLLING_INTERVAL);
      }
    }
  };

  const frequencyOptions = useMemo(
    () => [
      { value: Frequency.DAILY, label: t('sidebar.modals.requestNewLimits.frequencies.daily') },
      { value: Frequency.CYCLE, label: t('sidebar.modals.requestNewLimits.frequencies.cycle') },
      { value: Frequency.YEAR, label: t('sidebar.modals.requestNewLimits.frequencies.year') },
    ],
    [t],
  );
  const symbol = useCurrencySymbol(card.currency);
  const currentLimitValues = useMemo(() => {
    if (limits.frequency === Frequency.DAILY) return limits.dayLimits;
    else if (limits.frequency === Frequency.CYCLE) return limits.cycleLimits;
    else if (limits.frequency === Frequency.YEAR) return limits.yearLimits;
    else return limits.dayLimits;
  }, [limits, frequency]);
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={closeHandler}
      width={card.cardKind === CardKind.Credit ? '950px' : '600px'}
      title={t('sidebar.modals.requestNewLimits.title')}
      loading={cardLimitsStore.actionLoading || loading || cardLimitsStore.loading}
    >
      <FormProvider {...formControl}>
        <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
          <Controller
            name='frequency'
            control={control}
            rules={{}}
            render={({ field: { value, onChange } }) => (
              <Select
                className='max-w-[158px]'
                label={t('sidebar.modals.requestNewLimits.placeholders.limitFrequency')}
                items={frequencyOptions}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <div className='flex justify-between'>
            <CardNewLimitRow
              limitRowLabel={t('sidebar.modals.requestNewLimits.sections.retailLimit')}
              currentAmount={currentLimitValues?.retailAmount || 0}
              currentNumber={currentLimitValues?.retailNumber || 0}
              limitAmount={limits.currentRetailAmount}
              limitNumber={limits.currentRetailNumber}
              symbol={symbol}
              name='retail'
            />
            <CardNewLimitRow
              limitRowLabel={t('sidebar.modals.requestNewLimits.sections.cashLimit')}
              currentAmount={currentLimitValues?.cashAmount || 0}
              currentNumber={currentLimitValues?.cashNumber || 0}
              limitAmount={limits.currentCashAmount}
              limitNumber={limits.currentCashNumber}
              symbol={symbol}
              name='cash'
            />
            {card.cardKind === CardKind.Credit && (
              <CardNewLimitRow
                limitRowLabel={t('sidebar.modals.requestNewLimits.sections.totalLimit')}
                currentAmount={(currentLimitValues as CreditCardLimit)?.otcAmount || 0}
                currentNumber={(currentLimitValues as CreditCardLimit)?.otcNumber || 0}
                limitAmount={(limits as CreditLimitsResponse).currentOTCAmount}
                limitNumber={(limits as CreditLimitsResponse).currentOTCNumber}
                symbol={symbol}
                name='otc'
              />
            )}
          </div>
          <Button className='w-fit' color='primary' type='submit' disabled={formState.isSubmitting || loading || cardLimitsStore.loading}>
            {t('sidebar.modals.requestNewLimits.buttons.requestNewLimits')}
          </Button>
        </form>
      </FormProvider>
    </Modal>
  );
};
